// Side-effecting routine navigation for Slice 9.
// PRD: Ralph/NavigationalFeatures/PRD.md § Slice 9 (routine).
//
// F12 on a routine call (procedure / function) opens a query tab pre-loaded
// with the routine's CREATE script. The fetcher is the same
// `fetchObjectDefinitionScript` that the OE menu's "Script as CREATE"
// already calls, so all per-engine quirks (MSSQL `sys.sql_modules`, PG
// `pg_get_functiondef` on the OID, MySQL `SHOW CREATE PROCEDURE/FUNCTION`,
// Oracle `ALL_SOURCE`) flow through the established path. SQLite has no
// stored routines — its routine path is unreachable because the OE tree
// won't carry stored-procedure / function nodes for SQLite connections.
//
// The tab opens with a `-- Loading…` placeholder; once the script lands,
// `updateTabContent` patches the buffer in place. Mirrors `columnNavigation`
// view CREATE-text shape exactly.

import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import { getConnectionById } from '../../connections/utils/connectionLookup';
import type { DatabaseEngineId } from '@/core/engines';
import type { QualifiedName } from '../types';
import { defaultSchemaForEngine } from './externalNavigation';
import type { RoutineKind } from './routineAtCursor';
import { safeConnectionAlias, safeConnectionServer } from '@/types/safeConnectionDisplay';

export type RoutineNavigationTarget = {
  readonly qualified: QualifiedName;
  readonly routineKind: RoutineKind;
  readonly engineId: DatabaseEngineId;
  readonly connectionId: string;
  readonly database?: string;
  /** Engine introspection key (PG OID, etc.) — passed through to
   *  `fetchObjectDefinitionScript` which needs it for PG. Optional for
   *  engines that don't require an OID lookup. */
  readonly objectId?: string;
};

export type RoutineNavigationResult = {
  readonly opened: boolean;
  readonly tabId?: string;
};

/** Read-only probe: would `navigateToRoutine(target)` open a tab? Mirrors its
 *  connection guard. */
export function canNavigateToRoutine(target: RoutineNavigationTarget): boolean {
  return Boolean(getConnectionById(target.connectionId).connection);
}

/** Opens a query tab populated with the routine's CREATE script. Returns
 *  the tab id so tests can assert the tab exists. */
export function navigateToRoutine(
  target: RoutineNavigationTarget,
): RoutineNavigationResult {
  const { activeConnection, connection, engine } = getConnectionById(
    target.connectionId,
  );
  if (!connection) return { opened: false };

  const schema = target.qualified.schema ?? defaultSchemaForEngine(target.engineId);
  const database = target.database ?? target.qualified.database ?? '';

  const { addTab, getNextQueryNumber, updateTabContent } = useWorkspaceStore.getState();
  const queryNumber = getNextQueryNumber();
  const tabId = addTab({
    title: `${target.qualified.name}_CREATE-${queryNumber}`,
    type: 'query',
    content: {
      query: `-- Loading CREATE script for ${schema}.${target.qualified.name}...\n`,
      isGeneratingScript: true,
    },
    connectionInfo: {
      connectionId: target.connectionId,
      connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
      serverHost: safeConnectionServer(activeConnection?.connection),
      database,
    },
  });

  void loadRoutineDefinition({
    tabId,
    connectionId: target.connectionId,
    database,
    engine,
    schema,
    name: target.qualified.name,
    routineKind: target.routineKind,
    objectId: target.objectId,
    updateTabContent,
  });

  return { opened: true, tabId };
}

type LoadRoutineDefinitionArgs = {
  tabId: string;
  connectionId: string;
  database: string;
  engine: string;
  schema: string;
  name: string;
  routineKind: RoutineKind;
  objectId?: string;
  updateTabContent: (tabId: string, patch: Record<string, unknown>) => void;
};

async function loadRoutineDefinition(args: LoadRoutineDefinitionArgs): Promise<void> {
  try {
    const { fetchObjectDefinitionScript } = await import(
      '../../database-objects/services/objectDefinitionService'
    );
    const { script } = await fetchObjectDefinitionScript({
      connectionId: args.connectionId,
      database: args.database,
      engine: args.engine,
      objectType: args.routineKind,
      schema: args.schema,
      name: args.name,
      objectId: args.objectId,
      mode: 'create',
    });
    args.updateTabContent(args.tabId, {
      query: script,
      isGeneratingScript: false,
    });
  } catch (error) {
    args.updateTabContent(args.tabId, {
      query: `-- Error: ${error instanceof Error ? error.message : 'Unknown error'}\n`,
      isGeneratingScript: false,
    });
  }
}
